import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Terminal as XTerminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import { WebLinksAddon } from "@xterm/addon-web-links";
import "@xterm/xterm/css/xterm.css";
import {
  Plus, X, Server, Wifi, Loader2, SquareTerminal,
  Bot, Send, ChevronRight, Copy, Play, PanelRightClose, PanelRightOpen,
  RotateCcw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LAST_SERVER_KEY = "terminal_last_server";

function getWsUrl() {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return base.replace(/^https:/, "wss:").replace(/^http:/, "ws:");
}

// Parse code blocks from AI response
function parseAIResponse(text) {
  const parts = [];
  const codeRegex = /```(?:bash|sh|shell|cmd|powershell|)?\n?([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;
  while ((match = codeRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", content: text.slice(lastIndex, match.index) });
    }
    parts.push({ type: "code", content: match[1].trim() });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({ type: "text", content: text.slice(lastIndex) });
  }
  return parts;
}

function TerminalTab({ tab, isActive, onClose, onSelect, tabCount }) {
  return (
    <button
      onClick={onSelect}
      className={`flex items-center gap-2 px-3 py-1.5 text-xs font-mono rounded-t-lg border-b-2 transition-all ${
        isActive
          ? "bg-[#0a0a0a] text-white border-blue-500"
          : "bg-[#141414] text-zinc-500 border-transparent hover:text-zinc-300 hover:bg-[#1a1a1a]"
      }`}
      data-testid={`terminal-tab-${tab.id}`}
    >
      <div className={`w-2 h-2 rounded-full ${
        tab.status === "connected" ? "bg-green-400 animate-pulse" :
        tab.status === "connecting" ? "bg-yellow-400 animate-pulse" :
        tab.status === "error" ? "bg-red-400" : "bg-zinc-600"
      }`} />
      <span className="max-w-[120px] truncate">{tab.serverName || "New Tab"}</span>
      {tabCount > 1 && (
        <span onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="ml-1 p-0.5 rounded hover:bg-white/10 text-zinc-500 hover:text-red-400">
          <X className="w-3 h-3" />
        </span>
      )}
    </button>
  );
}

function TerminalPanel({ tab, servers, token, onStatusChange, onWsReady }) {
  const termRef = useRef(null);
  const termInstanceRef = useRef(null);
  const fitAddonRef = useRef(null);
  const wsRef = useRef(null);
  const containerRef = useRef(null);
  const [selectedServer, setSelectedServer] = useState(tab.serverId || "");

  const connect = useCallback((serverId) => {
    if (!serverId || !token) return;
    const server = servers.find(s => s.id === serverId);
    if (!server) return;

    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    if (termInstanceRef.current) { termInstanceRef.current.dispose(); termInstanceRef.current = null; }

    onStatusChange(tab.id, "connecting", server.name);
    localStorage.setItem(LAST_SERVER_KEY, serverId);

    const term = new XTerminal({
      cursorBlink: true,
      cursorStyle: "bar",
      fontSize: 13,
      fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
      theme: {
        background: "#09090b",
        foreground: "#e4e4e7",
        cursor: "#3b82f6",
        selectionBackground: "rgba(59,130,246,0.3)",
        black: "#27272a", red: "#ef4444", green: "#22c55e",
        yellow: "#eab308", blue: "#3b82f6", magenta: "#a855f7",
        cyan: "#06b6d4", white: "#e4e4e7",
        brightBlack: "#52525b", brightRed: "#f87171", brightGreen: "#4ade80",
        brightYellow: "#facc15", brightBlue: "#60a5fa", brightMagenta: "#c084fc",
        brightCyan: "#22d3ee", brightWhite: "#fafafa",
      },
      scrollback: 5000,
      allowProposedApi: true,
    });

    const fitAddon = new FitAddon();
    const webLinksAddon = new WebLinksAddon();
    term.loadAddon(fitAddon);
    term.loadAddon(webLinksAddon);
    termInstanceRef.current = term;
    fitAddonRef.current = fitAddon;

    if (containerRef.current) {
      term.open(containerRef.current);
      setTimeout(() => { try { fitAddon.fit(); } catch {} }, 100);
    }

    term.writeln("\x1b[34m●\x1b[0m Connecting to \x1b[1m" + server.name + "\x1b[0m (" + server.ip_address + ")...");

    const wsUrl = `${getWsUrl()}/api/ws/terminal/${serverId}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    onWsReady(tab.id, ws);

    ws.onopen = () => { ws.send(token); };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "ping") {
          // respond to keepalive
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "pong" }));
          return;
        }
        if (msg.type === "connected") {
          onStatusChange(tab.id, "connected", server.name);
          term.writeln("\x1b[32m●\x1b[0m " + msg.data);
          term.writeln("");
        } else if (msg.type === "output") {
          term.write(msg.data);
        } else if (msg.type === "error") {
          term.writeln("\x1b[31m●\x1b[0m Error: " + msg.data);
          onStatusChange(tab.id, "error", server.name);
        } else if (msg.type === "disconnected") {
          term.writeln("\r\n\x1b[33m●\x1b[0m " + msg.data);
          onStatusChange(tab.id, "disconnected", server.name);
        }
      } catch { term.write(event.data); }
    };

    ws.onerror = () => {
      term.writeln("\x1b[31m●\x1b[0m WebSocket connection error");
      onStatusChange(tab.id, "error", server.name);
    };

    ws.onclose = (e) => {
      if (e.code !== 1000) {
        term.writeln("\r\n\x1b[33m●\x1b[0m Connection closed (code: " + e.code + ")");
      }
      onStatusChange(tab.id, "disconnected", server.name);
      onWsReady(tab.id, null);
    };

    term.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "input", data }));
      }
    });

    term.onResize(({ cols, rows }) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "resize", cols, rows }));
      }
    });
  }, [servers, token, tab.id, onStatusChange, onWsReady]);

  const handleServerChange = (serverId) => {
    setSelectedServer(serverId);
    connect(serverId);
  };

  useEffect(() => {
    const handleResize = () => {
      if (fitAddonRef.current) { try { fitAddonRef.current.fit(); } catch {} }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (termInstanceRef.current) termInstanceRef.current.dispose();
    };
  }, []);

  useEffect(() => {
    if (tab.serverId && servers.length > 0) {
      setSelectedServer(tab.serverId);
      connect(tab.serverId);
    }
  }, [tab.serverId, servers, connect]);

  const reconnect = () => {
    if (selectedServer) connect(selectedServer);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-3 py-1.5 bg-[#0c0c0e] border-b border-white/5">
        <Select value={selectedServer} onValueChange={handleServerChange}>
          <SelectTrigger className="w-56 bg-black/50 border-white/10 text-white text-xs h-7" data-testid="ws-server-select">
            <SelectValue placeholder="Select server..." />
          </SelectTrigger>
          <SelectContent className="bg-[#0c0c0e] border-white/10 z-[9999]">
            {servers.length === 0 ? (
              <SelectItem value="none" disabled>No SSH servers configured</SelectItem>
            ) : (
              servers.map(s => (
                <SelectItem key={s.id} value={s.id}>
                  <span className="flex items-center gap-2 text-xs">
                    <Wifi className="w-3 h-3 text-green-400" />
                    {s.name} ({s.ip_address})
                  </span>
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        {selectedServer && (
          <button onClick={reconnect}
            className="text-zinc-400 hover:text-white p-1 rounded hover:bg-white/10 transition-colors"
            title="Reconnect" data-testid="reconnect-btn">
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div ref={containerRef} className="flex-1 bg-[#09090b] p-1" data-testid="xterm-container" />
    </div>
  );
}

// AI Panel Component
function AIPanel({ activeWs, serverOs, onClose }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! Ask me anything about server administration. I'll suggest commands you can run directly in the terminal." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(`terminal-ai-${Date.now()}`);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: text }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId.current,
        message: text,
        os_type: serverOs || "linux",
      });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Failed to get AI response. Check your AI settings." }]);
    } finally {
      setLoading(false);
    }
  };

  const runCommand = (cmd) => {
    if (!activeWs || activeWs.readyState !== WebSocket.OPEN) {
      toast.error("No active terminal connection. Select a server first.");
      return;
    }
    activeWs.send(JSON.stringify({ type: "input", data: cmd + "\n" }));
    toast.success("Command sent to terminal");
  };

  const copyCommand = (cmd) => {
    navigator.clipboard.writeText(cmd);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="flex flex-col h-full bg-[#0c0c0e] border-l border-white/5" data-testid="ai-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-medium text-white">AI Assistant</span>
        </div>
        <button onClick={onClose} className="text-zinc-500 hover:text-white p-1 rounded hover:bg-white/10">
          <PanelRightClose className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 scrollbar-thin">
        {messages.map((msg, i) => (
          <div key={i} className={`${msg.role === "user" ? "flex justify-end" : ""}`}>
            {msg.role === "user" ? (
              <div className="bg-blue-600/20 border border-blue-500/20 rounded-lg px-3 py-2 text-sm text-blue-100 max-w-[90%]">
                {msg.content}
              </div>
            ) : (
              <div className="space-y-2">
                {parseAIResponse(msg.content).map((part, j) => (
                  part.type === "text" ? (
                    <p key={j} className="text-xs text-zinc-300 leading-relaxed whitespace-pre-wrap">{part.content}</p>
                  ) : (
                    <div key={j} className="bg-black/60 border border-white/10 rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between px-2 py-1 bg-white/5 border-b border-white/5">
                        <span className="text-[10px] text-zinc-500 font-mono">command</span>
                        <div className="flex gap-1">
                          <button onClick={() => copyCommand(part.content)}
                            className="text-zinc-400 hover:text-white p-0.5 rounded hover:bg-white/10"
                            title="Copy" data-testid="copy-command-btn">
                            <Copy className="w-3 h-3" />
                          </button>
                          <button onClick={() => runCommand(part.content)}
                            className="text-green-400 hover:text-green-300 p-0.5 rounded hover:bg-green-500/10 flex items-center gap-1 text-[10px] font-medium"
                            title="Run in terminal" data-testid="run-command-btn">
                            <Play className="w-3 h-3" /> Run
                          </button>
                        </div>
                      </div>
                      <pre className="text-xs text-green-300 font-mono p-2 overflow-x-auto whitespace-pre-wrap">{part.content}</pre>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-zinc-500 text-xs">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>Thinking...</span>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="p-2 border-t border-white/5">
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
            }}
            placeholder="Ask e.g. 'how to check disk usage?'"
            className="flex-1 bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-600 resize-none h-16 focus:outline-none focus:border-blue-500/50"
            data-testid="ai-input"
          />
          <button onClick={sendMessage} disabled={loading || !input.trim()}
            className="self-end bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white px-3 py-2 rounded-lg transition-colors"
            data-testid="ai-send-btn">
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
        <p className="text-[10px] text-zinc-600 mt-1">Enter to send • Shift+Enter for newline</p>
      </div>
    </div>
  );
}

export default function DirectTerminal() {
  const { token } = useAuth();
  const [servers, setServers] = useState([]);
  const [tabs, setTabs] = useState([{ id: "tab-1", serverId: null, serverName: null, status: "idle" }]);
  const [activeTabId, setActiveTabId] = useState("tab-1");
  const [showAI, setShowAI] = useState(true);
  const wsMapRef = useRef({}); // tabId -> WebSocket

  useEffect(() => {
    axios.get(`${API}/servers`).then(res => {
      const sshServers = res.data.filter(s => s.ssh_configured);
      setServers(sshServers);
      // Auto-connect to last used server
      const lastServerId = localStorage.getItem(LAST_SERVER_KEY);
      if (lastServerId && sshServers.find(s => s.id === lastServerId)) {
        setTabs([{ id: "tab-1", serverId: lastServerId, serverName: null, status: "idle" }]);
      }
    }).catch(() => {});
  }, []);

  const addTab = (serverId = null) => {
    const id = `tab-${Date.now()}`;
    const server = serverId ? servers.find(s => s.id === serverId) : null;
    setTabs(prev => [...prev, { id, serverId, serverName: server?.name || null, status: "idle" }]);
    setActiveTabId(id);
  };

  const closeTab = (tabId) => {
    // cleanup ws
    if (wsMapRef.current[tabId]) { wsMapRef.current[tabId].close(); delete wsMapRef.current[tabId]; }
    setTabs(prev => {
      const next = prev.filter(t => t.id !== tabId);
      if (next.length === 0) {
        const newTab = { id: `tab-${Date.now()}`, serverId: null, serverName: null, status: "idle" };
        setActiveTabId(newTab.id);
        return [newTab];
      }
      if (activeTabId === tabId) setActiveTabId(next[next.length - 1].id);
      return next;
    });
  };

  const handleStatusChange = useCallback((tabId, status, serverName) => {
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, status, serverName: serverName || t.serverName } : t));
  }, []);

  const handleWsReady = useCallback((tabId, ws) => {
    if (ws) wsMapRef.current[tabId] = ws;
    else delete wsMapRef.current[tabId];
  }, []);

  const jwtToken = token || localStorage.getItem("token");

  const activeTab = tabs.find(t => t.id === activeTabId);
  const activeWs = wsMapRef.current[activeTabId] || null;
  const activeServer = servers.find(s => s.id === activeTab?.serverId);

  return (
    <div className="p-4 h-[calc(100vh-72px)] flex flex-col" data-testid="terminal-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <SquareTerminal className="w-6 h-6 text-blue-400" />
          <div>
            <h1 className="text-xl font-bold font-mono text-white tracking-tight">Terminal</h1>
            <p className="text-zinc-500 text-xs">Live SSH shell with AI assistant</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {servers.length > 0 && (
            <Select onValueChange={(v) => addTab(v)}>
              <SelectTrigger className="w-44 bg-black/50 border-white/10 text-white text-xs h-8" data-testid="quick-connect-select">
                <SelectValue placeholder="Quick connect..." />
              </SelectTrigger>
              <SelectContent className="bg-[#0c0c0e] border-white/10 z-[9999]">
                {servers.map(s => (
                  <SelectItem key={s.id} value={s.id}>
                    <span className="flex items-center gap-2 text-xs">
                      <Server className="w-3 h-3 text-green-400" /> {s.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button size="sm" variant="outline" onClick={() => addTab()}
            className="bg-white/5 border-white/10 hover:bg-white/10 text-white h-8 text-xs"
            data-testid="new-tab-btn">
            <Plus className="w-3 h-3 mr-1" /> New Tab
          </Button>
          <Button size="sm" variant="outline"
            onClick={() => setShowAI(v => !v)}
            className={`h-8 text-xs border-white/10 ${showAI ? "bg-blue-600/20 text-blue-400 border-blue-500/30" : "bg-white/5 text-white"}`}
            data-testid="toggle-ai-btn">
            {showAI ? <PanelRightClose className="w-3.5 h-3.5 mr-1" /> : <PanelRightOpen className="w-3.5 h-3.5 mr-1" />}
            AI
          </Button>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex items-center gap-0.5 bg-[#141414] rounded-t-lg border border-b-0 border-white/5 px-2 pt-1 overflow-x-auto">
        {tabs.map(tab => (
          <TerminalTab key={tab.id} tab={tab} isActive={activeTabId === tab.id}
            onClose={() => closeTab(tab.id)}
            onSelect={() => setActiveTabId(tab.id)}
            tabCount={tabs.length} />
        ))}
      </div>

      {/* Main Content: Terminal + AI Panel */}
      <div className="flex-1 flex overflow-hidden border border-white/5 rounded-b-lg">
        {/* Terminal panels */}
        <div className={`flex flex-col ${showAI ? "flex-1 min-w-0" : "w-full"}`}>
          {tabs.map(tab => (
            <div key={tab.id} className={`h-full ${activeTabId === tab.id ? "block" : "hidden"}`}>
              <TerminalPanel
                tab={tab}
                servers={servers}
                token={jwtToken}
                onStatusChange={handleStatusChange}
                onWsReady={handleWsReady}
              />
            </div>
          ))}
        </div>

        {/* AI Panel */}
        {showAI && (
          <div className="w-80 shrink-0 flex flex-col">
            <AIPanel
              activeWs={activeWs}
              serverOs={activeServer?.os_type || "linux"}
              onClose={() => setShowAI(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
